import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import TextInput from "../../components/TextInput";
import CheckboxGroup from "../../components/CheckboxGroup";
import SelectInput from "../../components/SelectInput";
import {
  FUENTE_INGRESOS,
  type FuenteIngresosOption,
  type IncorporationWizardInitialData,
} from "@/schemas/incorporation";
import countriesData from "../../components/countries.json";
import { createOrUpdateIncorporationRequest, IncorporationRequestError } from "@/services/incorporationRequest";

const FUENTE_INGRESOS_OTROS: FuenteIngresosOption = "otros";

// Zod schema for financial profile validation
const financialProfileSchema = z
  .object({
    fuente_ingresos: z
      .array(z.enum(FUENTE_INGRESOS))
      .min(1, "financial_profile.income_sources.required"),
    fuente_ingresos_otro: z.string().optional(),
    paises_ingresos: z
      .array(z.string())
      .min(1, "financial_profile.income_countries.required"),
    paises_actividad_economica: z
      .array(z.string())
      .min(1, "financial_profile.economic_activity_countries.required"),
    suma_aproximada_ingresos: z
      .string()
      .min(1, "financial_profile.annual_income.required"),
  })
  .refine(
    (data) => {
      // If "otros" is selected, fuente_ingresos_otro must be provided
      if (data.fuente_ingresos.includes(FUENTE_INGRESOS_OTROS)) {
        return (
          data.fuente_ingresos_otro !== undefined &&
          data.fuente_ingresos_otro.trim().length > 0
        );
      }
      return true;
    },
    {
      message: "financial_profile.income_sources.other_required",
      path: ["fuente_ingresos_otro"],
    }
  );

type FinancialProfileFormData = z.infer<typeof financialProfileSchema>;

// Income range options
const incomeRangeOptions = [
  { value: "0-50000", label: "$0 - $50,000" },
  { value: "50000-100000", label: "$50,000 - $100,000" },
  { value: "100000-250000", label: "$100,000 - $250,000" },
  { value: "250000-500000", label: "$250,000 - $500,000" },
  { value: "500000-1000000", label: "$500,000 - $1,000,000" },
  { value: "1000000+", label: "$1,000,000+" },
];

const FinancialProfileStep: StepComponentType = () => {
  const { t, i18n } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();
  const [saveError, setSaveError] = useState<string | null>(null);

  const { 
    control, 
    handleSubmit, 
    watch, 
    setError: setFormError,
    formState: { isSubmitting }
  } = useForm<FinancialProfileFormData>({
    resolver: zodResolver(financialProfileSchema),
    defaultValues: {
      fuente_ingresos: data?.incorporationRequest?.fuente_ingresos || [],
      fuente_ingresos_otro: data?.incorporationRequest?.fuente_ingresos_otro || "",
      paises_ingresos: data?.incorporationRequest?.paises_ingresos || [],
      paises_actividad_economica: data?.incorporationRequest?.paises_actividad_economica || [],
      suma_aproximada_ingresos: data?.incorporationRequest?.suma_aproximada_ingresos || "",
    },
  });

  const selectedFuenteIngresos = watch("fuente_ingresos");
  const hasOtrosSelected =
    selectedFuenteIngresos?.includes(FUENTE_INGRESOS_OTROS) || false;

  // Income source options for CheckboxGroup
  const fuenteIngresosOptions = FUENTE_INGRESOS.map((value) => ({
    value,
    label: t(`financial_profile.income_sources.${value}`),
  }));

  // Country options for SelectInput (use Spanish or English based on current language)
  const countryOptions = countriesData.map((country) => ({
    value: country.name_es,
    label: i18n.language === 'es' ? country.name_es : country.name_en,
  }));

  const onSubmit = async (formData: FinancialProfileFormData) => {
    setSaveError(null);

    try {
      // The incorporation request should already exist at this point (created in company-name-step)
      const incorporationRequestId = data?.incorporationRequest?.id;
      if (!incorporationRequestId) {
        throw new Error("La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior.");
      }

      const requestData = {
        ...data?.incorporationRequest,
        ...formData,
        id: incorporationRequestId, // Always include id for update
      };

      // Call API to update (request should already exist)
      const response = await createOrUpdateIncorporationRequest(requestData);

      // Update local state with response data
      setData(prevData => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          incorporationRequest: {
            ...prevData.incorporationRequest,
            ...response.data,
            id: response.id,
          },
          incorporationContact: prevData.incorporationContact || [],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });

      // Navigate to next step on success
      next();
    } catch (error) {
      // Handle different error types
      if (error instanceof IncorporationRequestError) {
        const errorMessage = error.errors && error.errors.length > 0
          ? error.errors.join(", ")
          : error.message;
        setSaveError(errorMessage);
        
        // For validation errors (422), try to set field-specific errors if possible
        if (error.statusCode === 422 && error.errors) {
          // Set a general form error - could be enhanced to map to specific fields
          setFormError("root", { message: errorMessage });
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : "Error al guardar los datos";
        setSaveError(errorMessage);
        setFormError("root", { message: errorMessage });
      }
      // Re-throw to prevent navigation on error
      throw error;
    }
  };

  return (
    <Layout
      title={t("financial_profile.title")}
      description={t("financial_profile.description")}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex flex-column gap-3 text-start">
          {/* Income Sources Section */}
          <CheckboxGroup
            name="fuente_ingresos"
            control={control}
            options={fuenteIngresosOptions}
          />

          {/* Conditional "Otros" Input */}
          {hasOtrosSelected && (
              <TextInput
              name="fuente_ingresos_otro"
                control={control}
                placeholder={t(
                  "financial_profile.income_sources.other_placeholder"
                )}
              />
          )}

          <p className="fw-bold text-body">
            {t("financial_profile.income_sources.title")}
          </p>

          {/* Income Countries Multi-Select */}
          <SelectInput
            name="paises_ingresos"
            control={control}
            label={t("financial_profile.income_countries.label")}
            placeholder={t("financial_profile.income_countries.placeholder")}
            options={countryOptions}
            isMulti={true}
          />

          {/* Economic Activity Countries Multi-Select */}
          <SelectInput
            name="paises_actividad_economica"
            control={control}
            label={t("financial_profile.economic_activity_countries.label")}
            placeholder={t(
              "financial_profile.economic_activity_countries.placeholder"
            )}
            options={countryOptions}
            isMulti={true}
          />

          {/* Annual Income Amount Single Select */}
          <SelectInput
            name="suma_aproximada_ingresos"
            control={control}
            label={t("financial_profile.annual_income.label")}
            placeholder={t("financial_profile.annual_income.placeholder")}
            options={incomeRangeOptions}
            isMulti={false}
          />

          {/* Error Message */}
          {saveError && (
            <div className="alert alert-danger" role="alert">
              {saveError}
            </div>
          )}

          {/* Continue Button */}
          <div className="d-flex justify-content-center">
            <NextButton
              type="submit"
              label={t("financial_profile.continue")}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </Layout>
  );
};

FinancialProfileStep.title = (t) => t("financial_profile.title");
FinancialProfileStep.description = (t) => t("financial_profile.description_progress_panel");
FinancialProfileStep.isComplete = (data: IncorporationWizardInitialData) => {
  // tal vez haya q manualmente normalizar esto en vez de pasar data directo
  // tal vez por los branches q hay diq si otros es seleccionado y vaina
  // pero veremos si hay bug o no
  return financialProfileSchema.safeParse(data.incorporationRequest).success;
};

export default FinancialProfileStep;
